import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import { Config, BOOLEAN_OPTIONS, Question } from '@ratehub/base-ui';

import { DisabilityCoverageAmountValues } from '../definitions/CurrentDocument';
import formatDisabilityCoverageAmount from '../functions/formatDisabilityCoverageAmount';
import QuestionStandardCity from './QuestionStandardCity';
import QuestionHasConsumedTobacco from './QuestionHasConsumedTobacco';
import QuestionWantsBundledQuote from './QuestionWantsBundledQuote';


function CoverageDisabilityQuestions({ values }) {
    return (
        <>
            {/* City */}
            <QuestionStandardCity
                city={values.city}
            />

            {/* Canadian citizen */}
            <Question
                field={{
                    name: 'isCanadianCitizen',
                    type: 'boolean',
                    value: values.isCanadianCitizen,
                    options: BOOLEAN_OPTIONS,
                }}
                type="radio"
                labelMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.isCanadianCitizen"
                        defaultMessage="Are you a Canadian citizen or permanent resident?"
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverDisabilityQuestions.isCanadianCitizenTooltip"
                        defaultMessage="If you are not a permanent resident or a Canadian citizen, you may not be eligible for disability coverage in Canada."
                    />
                )}
                shouldDisplayOptionsAsGrid
            />

            {/* Gross annual income */}
            <Question
                field={{
                    name: 'grossAnnualIncome',
                    value: values.grossAnnualIncome,
                    type: 'integer',
                }}
                type="currency"
                labelMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.grossAnnualIncome"
                        defaultMessage="What is your gross annual income before taxes?"
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.grossAnnualIncomeTooltip"
                        defaultMessage="Your gross income is part of the disability insurance calculation. The more you make annually, the more disability coverage you may require."
                    />
                )}
            />

            {/* Current occupation */}
            {/* Ticket created: INS-2718 */}
            <Question
                field={{
                    name: 'occupation',
                    value: values.occupation,
                    type: 'string',
                }}
                labelMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.occupationLabel"
                        defaultMessage="What is your current occupation?"
                    />
                )}
                placeholder={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.occupationPlaceholder"
                        defaultMessage="Job title and industry"
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.occupationTooltip"
                        defaultMessage="Please enter your current occupation. The type of job you have can impact your disability coverage calculation."
                    />
                )}
            />

            {/* How much disability coverage are you looking for? */}
            <Question
                field={{
                    name: 'disabilityCoverageAmount',
                    type: 'choice',
                    value: values.disabilityCoverageAmount,
                    options: DisabilityCoverageAmountValues,
                }}
                // This needs to be called directly as a function instead of a react component
                // to return the <FormattedMessage> correctly
                renderOptionLabel={value => formatDisabilityCoverageAmount(value)}
                labelMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.disabilityCoverageAmountLabel"
                        defaultMessage="How much monthly disability coverage are you looking for?"
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.CoverageDisabilityQuestions.disabilityCoverageAmountTooltip"
                        defaultMessage="Please select the approximate amount of coverage you need on your policy. Don't worry, you can discuss the amount further with a broker before finalizing your policy."
                    />
                )}
            />

            {/* Tobacco */}
            <QuestionHasConsumedTobacco
                value={values.hasConsumedTobacco}
            />

            {/* Cleanup in INS-3191 */}
            <If condition={Config.ENABLE_INS_LIFE_BUNDLE_QUOTE_QUESTION}>
                {/* Free auto or home quote */}
                <QuestionWantsBundledQuote
                    value={values.wantsBundledQuote}
                />
            </If>
        </>
    );
}

CoverageDisabilityQuestions.propTypes = {
    values: PropTypes.shape({
        city: PropTypes.object,
        isCanadianCitizen: PropTypes.bool,
        grossAnnualIncome: PropTypes.number,
        occupation: PropTypes.string,
        disabilityCoverageAmount: PropTypes.oneOf(DisabilityCoverageAmountValues),
        hasConsumedTobacco: PropTypes.bool,
        wantsBundledQuote: PropTypes.bool,
    }).isRequired,
};

export default observer(CoverageDisabilityQuestions);
